import { ArrowLeft, ExternalLink } from 'react-feather';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { AddParticipantFieldImportMutation as AddParticipantFieldImport } from '__generated__/graphql';

import { assetUrl, encodeUrl, isGraphQLValidationError } from '../../../common/helpers';
import AddParticipantFieldImportForm from './AddParticipantFieldImportForm';
import EventPicker, { Event } from '../../Events/EventPicker';
import GetEventsCountQuery from '../GetEventsCountQuery';
import GetParticipantFieldImportsForEventQuery from './GetParticipantFieldImportsForEventQuery';
import NoEventsCard from '../NoEventsCard';
import ParticipantFieldImportCard from './ParticipantFieldImportCard';
import UI from '../../../common/UI';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useRecentEventId from '../../Common/useRecentEventId';
import useScroll from '../../../common/useScroll';

export interface ParticipantFieldImportsPageProps {
  participantFieldImportId?: string;
}

const ParticipantFieldImportsPage = ({ participantFieldImportId }: ParticipantFieldImportsPageProps) => {
  const { t } = useTranslation();
  const { scrollToIfInvisible } = useScroll();
  const history = useHistory();
  const project = useProject();
  const recentEvent = useRecentEventId();

  useDocumentTitle(t('imports'));

  const { data: eventsData } = useQuery(
    GetEventsCountQuery,
    {
      variables: {
        project_id: project.id,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const { data, loading, refetch } = useQuery(
    GetParticipantFieldImportsForEventQuery,
    {
      variables: {
        project_id: project.id,
        event_id: recentEvent.id,
      },
      skip: !recentEvent.id,
      onError: (error) => {
        if (isGraphQLValidationError(error)) {
          // Recent event no longer exists, reset it.
          recentEvent.reset();
        }
      },
    },
  );

  const event = data?.project.event.id === recentEvent.id ? data?.project.event : null;
  const participantFieldImports = event?.participant_field_imports;

  const [openAddParticipantFieldImportModal, closeAddParticipantFieldImportModal] = useModal(() => {
    const handleSuccess = (participantFieldImport: AddParticipantFieldImport['addParticipantFieldImport']) => {
      closeAddParticipantFieldImportModal();
      history.push(`/${project.id}/participants/imports/${participantFieldImport.id}?${encodeUrl({ event: recentEvent.id })}`);
    };

    return (
      <UI.Modal maxWidth={600} close={closeAddParticipantFieldImportModal}>
        <AddParticipantFieldImportForm
          event={event}
          onSuccess={handleSuccess}
        />
      </UI.Modal>
    );
  }, [project, event]);

  useEffect(() => {
    if (event && participantFieldImports.filter(({ id }) => id === participantFieldImportId).length === 0) {
      refetch().then(() => {
        scrollToIfInvisible(`ParticipantFieldImportCard_${participantFieldImportId}`, { gutter: 0.5 });
      });
    }
  }, [participantFieldImportId]);

  const handleEventChange = (event: Event) => {
    history.push(`/${project.id}/participants/imports?${encodeUrl({ event: event.id })}`);
  };

  if (!eventsData || (recentEvent.id && !event)) {
    return <UI.PageLoader />;
  }

  if (eventsData.project.events.total === 0) {
    return <NoEventsCard />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.FlexContainer
          sc={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
        >
          <UI.SectionHeading sc={{ inlineBlock: true }}>
            {t('imports')}
          </UI.SectionHeading>
        </UI.FlexContainer>

        <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'], alignVertical: 'center' }} style={{ zIndex: 10 }}>
          <UI.Div>
            <EventPicker
              eventId={recentEvent.id}
              onChange={handleEventChange}
              nullable={false}
              renderActive={(event) => (
                <UI.Span sc={{ color: 'gray.800' }} style={{ fontWeight: '500' }}>
                  {event?.title || t('choose_event')}
                </UI.Span>
              )}
            />
          </UI.Div>
          {!recentEvent.id && (
            <UI.Div sc={{ color: 'gray.500' }}>
              <UI.Icon>
                <ArrowLeft />
              </UI.Icon>
              {' '}
              {t('select_event_first')}
            </UI.Div>
          )}
          {event && participantFieldImports.length > 0 && (
            <UI.Div sc={{ textAlign: 'right' }}>
              <UI.Button
                onClick={openAddParticipantFieldImportModal}
                sc={{ brand: 'secondary' }}
              >
                {t('add_import')}
              </UI.Button>
            </UI.Div>
          )}
        </UI.GridContainer>

        {event && (
          <UI.GridContainer sc={{ muted: loading, gutter: 1 / 3 }} style={{ zIndex: 1 }}>
            {participantFieldImports.map((participantFieldImport) => (
              <ParticipantFieldImportCard
                participantFieldImport={participantFieldImport}
                active={participantFieldImportId === participantFieldImport.id}
                muted={participantFieldImportId && participantFieldImportId !== participantFieldImport.id}
                key={participantFieldImport.id}
              />
            ))}
            {participantFieldImports.length === 0 && (
              <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }} style={{ textWrap: 'balance' }}>
                <img src={assetUrl('images/participant.svg')} alt="" height={120} />
                <UI.H3 sc={{ mb: 2 }}>{t('no_participant_field_imports_yet')}</UI.H3>
                <UI.Paragraph sc={{ mb: 1 }}>
                  {t('no_participant_field_imports_get_started')}
                </UI.Paragraph>
                <UI.Paragraph sc={{ mb: 1 }}>
                  <UI.A
                    href="https://about.atleta.cc/article/import-participants"
                    target="_blank"
                    rel="noopener noreferer"
                  >
                    {t('how_to_import_participants')}
                    {' '}
                    <UI.Icon>
                      <ExternalLink />
                    </UI.Icon>
                  </UI.A>
                </UI.Paragraph>
                <UI.Paragraph>
                  <UI.A
                    href="https://about.atleta.cc/article/import-custom-fields"
                    target="_blank"
                    rel="noopener noreferer"
                  >
                    {t('how_to_import_participant_field_entries')}
                    {' '}
                    <UI.Icon>
                      <ExternalLink />
                    </UI.Icon>
                  </UI.A>
                </UI.Paragraph>
                <UI.Button
                  onClick={() => openAddParticipantFieldImportModal()}
                  sc={{ brand: 'secondary' }}
                >
                  {t('add_import')}
                </UI.Button>
              </UI.Card>
            )}
          </UI.GridContainer>
        )}

        {!recentEvent.id && (
          <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }} style={{ textWrap: 'balance' }}>
            <img src={assetUrl('images/participant.svg')} alt="" height={120} />
            <UI.H3 sc={{ mb: 2 }}>{t('no_participant_field_imports_yet')}</UI.H3>
            <UI.Paragraph sc={{ mb: 1 }}>
              {t('no_participant_field_imports_get_started')}
            </UI.Paragraph>
            <UI.Paragraph sc={{ mb: 1 }}>
              <UI.A href="https://about.atleta.cc/article/import-participants" target="_blank" rel="noopener noreferer">
                {t('how_to_import_participants')}
                {' '}
                <UI.Icon>
                  <ExternalLink />
                </UI.Icon>
              </UI.A>
            </UI.Paragraph>
            <UI.Paragraph sc={{ mb: 0 }}>
              <UI.A
                href="https://about.atleta.cc/article/import-custom-fields"
                target="_blank"
                rel="noopener noreferer"
              >
                {t('how_to_import_participant_field_entries')}
                {' '}
                <UI.Icon>
                  <ExternalLink />
                </UI.Icon>
              </UI.A>
            </UI.Paragraph>
          </UI.Card>
        )}
      </UI.GridContainer>
    </UI.Section>
  );
};

export default ParticipantFieldImportsPage;
